import type { TaxIdEntry, TaxIdInput, VendorSearchResponse } from '@schemas/api';

export const TAX_ID_TYPE_OPTIONS = [
  { value: 'US_EIN', label: 'EIN', jurisdiction: 'US', category: 'taxpayer_identity' },
  { value: 'US_SSN', label: 'SSN', jurisdiction: 'US', category: 'taxpayer_identity' },
  { value: 'US_ITIN', label: 'ITIN', jurisdiction: 'US', category: 'taxpayer_identity' },
  { value: 'CA_GST', label: 'GST', jurisdiction: 'CA', category: 'tax_registration' },
  { value: 'CA_QST', label: 'QST', jurisdiction: 'CA', category: 'tax_registration' },
  { value: 'CA_HST', label: 'HST', jurisdiction: 'CA', category: 'tax_line' },
  { value: 'CA_PST', label: 'PST', jurisdiction: 'CA', category: 'tax_line' },
  { value: 'EU_VAT', label: 'VAT', jurisdiction: 'INTL', category: 'tax_registration' },
] as const;

export const VENDOR_TAX_ID_OPTIONS = TAX_ID_TYPE_OPTIONS.filter((option) =>
  option.category === 'taxpayer_identity' || option.category === 'tax_registration'
);

export const W9_TAX_ID_OPTIONS = TAX_ID_TYPE_OPTIONS.filter((option) =>
  option.category === 'taxpayer_identity'
);

const TAX_ID_LABELS: Record<string, string> = Object.fromEntries(
  TAX_ID_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

const TAX_ID_OPTIONS_BY_KEY = Object.fromEntries(
  TAX_ID_TYPE_OPTIONS.map((option) => [option.value, option])
);

const TAX_ID_PRIORITY = ['US_EIN', 'US_SSN', 'US_ITIN', 'CA_QST', 'CA_GST', 'EU_VAT'];
const DEFAULT_TAX_ID_TYPE = 'US_EIN';

export function isMaskedSensitiveValue(value?: string | null): boolean {
  const candidate = String(value || '').trim();
  return candidate === '[REDACTED]' || candidate === '[OMITTED]' || /^(?:\*{3,}|•{3,}|x{3,}|#{3,})/i.test(candidate);
}

export interface VendorTaxIdSelection {
  type: string;
  value: string;
}

export function normalizeTaxIdEntries(taxIds?: TaxIdInput): TaxIdEntry[] {
  const rawEntries = Array.isArray(taxIds)
    ? taxIds
    : Object.entries(taxIds || {}).map(([type, value]) => ({ type, value }));

  const seen = new Set<string>();
  return rawEntries
    .map((entry) => ({
      type: String((entry as any).type || (entry as any).key || '').trim().toUpperCase(),
      value: String((entry as any).value || '').trim(),
      verified: (entry as any).verified,
      verification_status: (entry as any).verification_status,
      verified_at: (entry as any).verified_at,
    }))
    .filter((entry) => entry.type && entry.value)
    .filter((entry) => {
      if (seen.has(entry.type)) return false;
      seen.add(entry.type);
      return true;
    })
    .map((entry) => {
      const option = TAX_ID_OPTIONS_BY_KEY[entry.type as keyof typeof TAX_ID_OPTIONS_BY_KEY];
      return {
        ...entry,
        label: option?.label || entry.type,
        jurisdiction: option?.jurisdiction,
        category: option?.category || 'tax_registration',
      };
    });
}

export function taxIdEntriesToDict(taxIds?: TaxIdInput): Record<string, string> {
  return Object.fromEntries(normalizeTaxIdEntries(taxIds).map((entry) => [entry.type, entry.value]));
}

export function buildVendorTaxIdRows(taxIds?: TaxIdInput): VendorTaxIdSelection[] {
  const entries = normalizeTaxIdEntries(taxIds);
  const byType = Object.fromEntries(entries.map((entry) => [entry.type, entry.value]));
  const rows: VendorTaxIdSelection[] = [];

  TAX_ID_PRIORITY.forEach((type) => {
    if (byType[type]) {
      rows.push({ type, value: byType[type] });
      delete byType[type];
    }
  });

  Object.entries(byType).forEach(([type, value]) => rows.push({ type, value }));
  return rows.length > 0 ? rows : [{ type: DEFAULT_TAX_ID_TYPE, value: '' }];
}

export function buildVendorTaxIdsFromRows(
  rows?: Array<{ type?: string; value?: string }>
): TaxIdEntry[] | undefined {
  const entries = normalizeTaxIdEntries(
    (rows || [])
      .filter((row): row is { type: string; value: string } => Boolean(row.type?.trim() && row.value?.trim()))
      .filter((row) => !isMaskedSensitiveValue(row.value))
      .map((row) => ({ type: row.type, value: row.value }))
  );
  return entries.length > 0 ? entries : undefined;
}

export function getPreferredVendorTaxId(taxIds?: TaxIdInput): VendorTaxIdSelection {
  const [firstTaxId] = buildVendorTaxIdRows(taxIds);
  return firstTaxId || { type: DEFAULT_TAX_ID_TYPE, value: '' };
}

export function buildVendorTaxIds(params: {
  currentType?: string;
  currentValue?: string;
  initialTaxIds?: TaxIdInput;
  initialType?: string;
}): TaxIdEntry[] | undefined {
  const nextTaxIds = taxIdEntriesToDict(params.initialTaxIds);
  const initialType = params.initialType?.trim().toUpperCase();
  const currentType = params.currentType?.trim().toUpperCase();
  const currentValue = params.currentValue?.trim();

  if (initialType && initialType !== currentType) {
    delete nextTaxIds[initialType];
  }
  if (currentType && currentValue && !isMaskedSensitiveValue(currentValue)) {
    nextTaxIds[currentType] = currentValue;
  } else if (currentType) {
    delete nextTaxIds[currentType];
  } else if (initialType) {
    delete nextTaxIds[initialType];
  }

  const entries = normalizeTaxIdEntries(nextTaxIds).filter(
    (entry) => !isMaskedSensitiveValue(entry.value)
  );
  return entries.length > 0 ? entries : undefined;
}

export function formatVendorTaxIds(taxIds?: TaxIdInput): string {
  const entries = normalizeTaxIdEntries(taxIds);
  if (entries.length === 0) {
    return '-';
  }

  return entries
    .map((entry) => `${TAX_ID_LABELS[entry.type] || entry.label || entry.type}: ${entry.value}`)
    .join(', ');
}

export function getVendorReferenceText(vendor: Partial<VendorSearchResponse> & Record<string, any>): string {
  if (vendor.vendor_number) {
    return String(vendor.vendor_number);
  }

  return formatVendorTaxIds(vendor.tax_ids).replace(/^-$/, '');
}
